import { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const SectionOverlap = ({
  children,
  overlapType = "slide", // "slide", "fade", "scale", "parallax"
  overlapDistance = 100,
  overlapDirection = "up", // "up", "down", "left", "right"
  overlapDuration = 1,
  overlapEase = "power2.out",
  triggerOffset = 0,
  zIndex = 10,
  className = "",
}) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Set initial z-index for overlap
    gsap.set(el, { zIndex });

    // Determine the axis and direction for overlap
    const isHorizontal = overlapDirection === "left" || overlapDirection === "right";
    const axis = isHorizontal ? "x" : "y";
    const direction = overlapDirection === "up" || overlapDirection === "left" ? 1 : -1;

    // Create the overlap animation
    const createOverlapAnimation = () => {
      switch (overlapType) {
        case "slide":
          return gsap.to(el, {
            [axis]: overlapDistance * direction,
            duration: overlapDuration,
            ease: overlapEase,
            scrollTrigger: {
              trigger: el,
              start: `top ${100 - triggerOffset}%`,
              end: `bottom ${triggerOffset}%`,
              scrub: true,
            },
          });

        case "fade":
          return gsap.to(el, {
            opacity: 0.3,
            duration: overlapDuration,
            ease: overlapEase,
            scrollTrigger: {
              trigger: el,
              start: `top ${100 - triggerOffset}%`,
              end: `bottom ${triggerOffset}%`,
              scrub: true,
            },
          });

        case "scale":
          return gsap.to(el, {
            scale: 0.95,
            duration: overlapDuration,
            ease: overlapEase,
            scrollTrigger: {
              trigger: el,
              start: `top ${100 - triggerOffset}%`,
              end: `bottom ${triggerOffset}%`,
              scrub: true,
            },
          });

        case "parallax":
          return gsap.to(el, {
            y: overlapDistance * 0.5,
            duration: overlapDuration,
            ease: "none",
            scrollTrigger: {
              trigger: el,
              start: "top bottom",
              end: "bottom top",
              scrub: true,
            },
          });

        default:
          return null;
      }
    };

    const overlapTween = createOverlapAnimation();

    return () => {
      if (overlapTween) {
        overlapTween.kill();
      }
      ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, [overlapType, overlapDistance, overlapDirection, overlapDuration, overlapEase, triggerOffset, zIndex]);

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
};

export default SectionOverlap;
